import { useUserStore, useMarketplaceStore } from "@/console-new/stores";
import { useNotificationStore } from "@/shared/stores";

const ordersPermissions = [
	'orders',
	'orders_creation',
	'orders_edit',
	'orders_pay_pos',
	'orders_pay_cash',
	'orders_pay_other',
	'orders_discounts',
	'orders_daily_closing'
];

const allPermissions = [
	'admin',
	'analytics',
	'menu',
	...ordersPermissions
];

const oldPlans = ['subscription', 'order_pay', 'pos', 'counter', 'pay_bill', 'menu', 'delivery', 'takeaway', 'kitchen_monitor'];
const newPlans = ['free', 'basic', 'pro', 'allinone', 'enterprise'];

const routes = {
	'/ops': {
		children: {
			'/orders': {
				permissions: ordersPermissions,
				plansOld: ['order_pay', 'pos', 'counter', 'pay_bill'],
				plans: newPlans,
			},
			'/tables': {
				permissions: ordersPermissions,
				plansOld: ['order_pay', 'pay_bill'],
				plans: ['pro', 'allinone', 'enterprise'],
			},
			'/counter': {
				permissions: ordersPermissions,
				plansOld: ['counter'],
				plans: newPlans,
			}
		}
	},
	'/analytics': {
		children: {
			'/orders': {
				permissions: ['analytics'],
				plansOld: ['subscription'],
				plans: newPlans,
			},
			'/products': {
				permissions: ['analytics'],
				plansOld: ['order_pay', 'counter'],
				plans: newPlans,
			},
			'/views': {
				permissions: ['analytics'],
				plansOld: ['subscription'],
				plans: ['basic', 'pro', 'allinone', 'enterprise'],
			},
			'/tables': {
				permissions: ['analytics'],
				plansOld: ['order_pay', 'pay_bill'],
				plans: ['pro', 'allinone', 'enterprise'],
			}
		}
	},
	'/marketing': {
		children: {
			'/newsletter': {
				permissions: ['marketing'],
				plansOld: [null],
				plans: newPlans,
			},
			'/campaigns': {
				permissions: ['marketing'],
				plansOld: [null],
				plans: newPlans,
			},
			'/coupons': {
				permissions: ['admin', 'marketing'],
				plansOld: ['order_pay', 'counter'],
				plans: ['pro', 'allinone', 'enterprise'],
			}
		}
	},
	'/config': {
		children: {
			'/menu': {
				permissions: ['admin', 'menu'],
				plansOld: oldPlans,
				plans: [null, ...newPlans],
			},
			'/qrcode': {
				permissions: ['admin', 'menu'],
				plansOld: oldPlans,
				plans: ['basic', 'pro', 'allinone', 'enterprise'],
			},
			'/administration': {
				children: {
					'/stripe': {
						permissions: ['admin'],
						plansOld: ["order_pay", "pos", "counter", "pay_bill"],
						plans: newPlans,
					},
					'/qromo': {
						permissions: ['admin'],
						plansOld: ["order_pay", "pos", "counter", "pay_bill"],
						plans: newPlans,
					},
					'/taxation': {
						permissions: ['admin'],
						plansOld: ["order_pay", "pos", "counter", "pay_bill"],
						plans: newPlans,
					},
					'/cards': {
						permissions: ['admin'],
						plansOld: [],
						plans: newPlans,
					}
				}
			},
			'/settings': {
				children: {
					'/counter': {
						permissions: ['admin'],
						plansOld: ['counter'],
						plans: [null, ...newPlans],
					},
					'/general': {
						permissions: ['admin'],
						plansOld: oldPlans,
						plans: [null, ...newPlans],
					}
				}
			},
			'/devices': {
				children: {
					'/pos': {
						permissions: ['admin'],
						plansOld: ['pos'],
						plans: [null, ...newPlans],
					},
					'/printers': {
						permissions: ['admin'],
						plansOld: ['order_pay', 'pos', 'counter', 'pay_bill'],
						plans: [null, ...newPlans],
					}
				}
			},
			'/warehouse': {
				permissions: ['admin', 'menu'],
				plansOld: ['counter'],
				plans: ['pro', 'allinone', 'enterprise'],
			}
		}
	},
	'/plugins': {
		children: {
			'/shop': {
				permissions: ['admin'],
				plansOld: oldPlans,
				plans: [null, ...newPlans],
			},
			'/menu': {
				permissions: ['admin'],
				plansOld: oldPlans,
				plans: ['basic', 'pro', 'allinone', 'enterprise'],
			},
			'/orders': {
				children: {
					'/features': {
						permissions: ['admin'],
						plansOld: ['order_pay'],
						plans: ['pro', 'allinone', 'enterprise'],
					},
					'/payments': {
						permissions: ['admin'],
						plansOld: ['order_pay', 'pay_bill'],
						plans: ['pro', 'allinone', 'enterprise'],
					}
				}
			},
			'/reservations': {
				permissions: ['admin'],
				plansOld: ['kitchen_monitor'],
				plans: ['allinone', 'enterprise'],
			},
			'/delivery': {
				permissions: ['admin'],
				plansOld: ['delivery'],
				plans: ['pro', 'allinone', 'enterprise'],
			},
			'/takeaway': {
				permissions: ['admin'],
				plansOld: ['takeaway'],
				plans: ['pro', 'allinone', 'enterprise'],
			},
			'/kitchen': {
				permissions: ['admin'],
				plansOld: ['kitchen_monitor'],
				plans: ['allinone', 'enterprise'],
			}
		}
	},
	'/help': {
		children: {
			'/chat': {
				permissions: allPermissions,
				plansOld: ['subscription'],
				plans: [null, ...newPlans],
			}
		}
	},
	'/lost': {
		permissions: allPermissions,
		plansOld: oldPlans,
		plans: [null, ...newPlans],
	},
	'/add-business': {
		permissions: allPermissions,
		plansOld: oldPlans,
		plans: [null, ...newPlans],
	}
};

// Build route map
function buildRouteMap(routeObj, parentPath = '', parentInfo = {}) {
	const map = {};

	for (const [key, value] of Object.entries(routeObj)) {
		const currentPath = parentPath + key;

		// Start with current route's own properties
		let currentInfo = {
			permissions: value.permissions || [],
			plansOld: value.plansOld || [],
			plans: value.plans || []
		};

		// If has children, aggregate all children's permissions and plans
		if (value.children) {
			const childrenInfo = aggregateChildrenInfo(value.children);

			// Merge current with aggregated children (union for all)
			currentInfo = {
				permissions: [...new Set([...currentInfo.permissions, ...childrenInfo.permissions])],
				plansOld: [...new Set([...currentInfo.plansOld, ...childrenInfo.plansOld])],
				plans: [...new Set([...currentInfo.plans, ...childrenInfo.plans])]
			};

			// Process children recursively for their own entries
			Object.assign(map, buildRouteMap(value.children, currentPath, {}));
		}

		map[currentPath] = currentInfo;
	}

	return map;
}

function aggregateChildrenInfo(childrenObj) {
	let allPermissions = [];
	let allPlansOld = [];
	let allPlans = [];

	for (const [key, value] of Object.entries(childrenObj)) {
		// Add current child's properties
		if (value.permissions) allPermissions.push(...value.permissions);
		if (value.plansOld) allPlansOld.push(...value.plansOld);
		if (value.plans) allPlans.push(...value.plans);

		// Recursively aggregate nested children
		if (value.children) {
			const nestedInfo = aggregateChildrenInfo(value.children);
			allPermissions.push(...nestedInfo.permissions);
			allPlansOld.push(...nestedInfo.plansOld);
			allPlans.push(...nestedInfo.plans);
		}
	}

	return {
		permissions: allPermissions,
		plansOld: allPlansOld,
		plans: allPlans,
	};
}

// Build the route map once
const routeMap = buildRouteMap(routes);

// Get route info for a given path
export function getRouteInfo(routePath) {
	if (!routePath) return null;

	// Direct O(1) lookup
	if (routeMap[routePath]) {
		return routeMap[routePath];
	}

	// Find longest matching prefix
	let longestMatch = null;
	let longestLength = 0;

	for (const path in routeMap) {
		if (routePath.startsWith(path) && path.length > longestLength) {
			longestMatch = routeMap[path];
			longestLength = path.length;
		}
	}

	return longestMatch;
}

// Check if a plan is allowed for a route
export function isPlanAllowed(routeInfo) {
	const userStore = useUserStore();

	if (!routeInfo || !userStore.currBusiness) {
		return false; // Deny by default if missing info
	}

	let allowed = true;
	const activePlan = userStore.currBusiness.active_plan;

	if (activePlan === "old") {
		if (routeInfo.plansOld && routeInfo.plansOld.length) {
			allowed = routeInfo.plansOld.some(v => userStore.currPlans[v]);
			if (!allowed && routeInfo.plansOld.includes('subscription')) {
				allowed = userStore.currBusiness.active_plan !== null;
			}
		}
	} else {
		if (routeInfo.plans && routeInfo.plans.length) {
			allowed = routeInfo.plans.includes(userStore.currBusiness.active_plan);
		}
	}

	return allowed;
}

// Check if user has permission for a route
export function hasPermissionForRoute(routePath) {
	const userStore = useUserStore();
	const routeInfo = getRouteInfo(routePath);

	if (!routeInfo) {
		return true; // Allow if no route info found (not restricted)
	}

	// Check permissions
	if (routeInfo.permissions && routeInfo.permissions.length) {
		const hasPermission = routeInfo.permissions.some(v => userStore.currPermissions.includes(v));
		if (!hasPermission) {
			return false;
		}
	}

	return true;
}

// Check if a route is locked due to plan restrictions
export function isRouteLockedByPlan(routePath) {
	const routeInfo = getRouteInfo(routePath);

	if (!routeInfo) {
		return false; // Not locked if no route info found
	}

	// Only check plan requirements, not permissions
	return !isPlanAllowed(routeInfo);
}

// Handle click on locked route - navigate to marketplace and show notification
export function handleLockedRoute(router) {
	const notificationStore = useNotificationStore();

	// Show generic plan restriction notification
	notificationStore.showNotification('plan-locked-generic');

	// Navigate to marketplace
	router.push('/plugins/marketplace');
}

// Get all available routes from the route map
export function getAllRoutes() {
	return Object.keys(routeMap);
}

// Export route constants for use in other components
export { ordersPermissions, allPermissions, oldPlans, newPlans };